import React from "react";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { Flex } from "styles/utility-components";

import RecordCardList from "components/feed/RecordCardList";
import { RecordPreviewHeading } from "components/feed/RecordPreviewHeading";
import {
  DEFAULT_PREVIEW_LOOKBACK_MONTHS,
  useFeedPreviewNotices,
} from "lib/hooks/api/useFeedPreviewNotices";
import { SelfServeQuery } from "lib/StotlesApi";

export const DEFAULT_PREVIEW_LOOKBACK = DateTime.now()
  .startOf("day")
  .minus({ months: DEFAULT_PREVIEW_LOOKBACK_MONTHS });

// Renders the notice-list preview from app-service. `companyId` previews another
// company's feed (admin only). Per-signal counts come from useSignalPreviewCounts.
type Props = {
  feedCriteria: SelfServeQuery;
  companyId?: number;
};

export function FeedSettingsPreview({ feedCriteria, companyId }: Props): JSX.Element {
  const { isLoading, isError, previewRecords, totalResults } = useFeedPreviewNotices(
    feedCriteria,
    companyId,
  );

  const monthlyAverageResults =
    (totalResults / DateTime.now().diff(DEFAULT_PREVIEW_LOOKBACK, "days").days) * 30.5;

  return (
    <Container column>
      <Header>
        <RecordPreviewHeading
          totalResults={totalResults}
          displayedResults={previewRecords.length}
          isLoading={isLoading}
          isError={isError}
          monthlyAverageResults={monthlyAverageResults}
          recordListCountLabel={`in last ${DEFAULT_PREVIEW_LOOKBACK_MONTHS} months`}
        />
      </Header>
      <ListContainer>
        <RecordCardList previewRecords={previewRecords} feedCriteria={feedCriteria} />
      </ListContainer>
    </Container>
  );
}

const Container = styled(Flex)(({ theme }) => ({
  height: "100%",
  backgroundColor: theme.colors.sysBackgroundDefault,
  padding: 16,
  borderRadius: 8,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

const Header = styled.div({
  marginBottom: 16,
  flex: "0 0 auto",
});

const ListContainer = styled.div({
  flex: "1 1 auto",
  overflowY: "auto",
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
});
